import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Image from 'next/image';
import { useMediaQuery } from '../../../../hooks/mediaQuery';
import { BlogCategoryImage } from '../../../../pages/blog';
import Link from 'next/link';
import { useRouter } from 'next/router';

const ImageGridContainer = styled.div`
  display: flex;
  flex-direction: column;

  .row {
    display: flex;
    gap: 20px;
  }

  @media (max-width: 1023px) {
    gap: 16px;
    .row {
      display: flex;
      gap: 16px;
      height: 256px;
    }

    .row-1 .image-1,
    .row-1 .image-2 {
      width: 50%;
    }

    .row-2 .image-3 {
      width: 100%;
    }

    .row-3 .image-4,
    .row-3 .image-5 {
      width: 50%;
    }

    .row-4 .image-6 {
      width: 40%;
    }
    .row-4 .image-7 {
      width: 60%;
    }
    .row-5 .image-8,
    .row-5 .image-9 {
      width: 50%;
    }
  }

  @media (max-width: 540px) {
    gap: 12px;
    .row {
      display: flex;
      gap: 12px;
      height: 150px;
    }
  }

  @media (min-width: 1024px) {
    gap: 20px;
    .row {
      display: flex;
      gap: 20px;
      height: 358px;
    }

    .row-1 .image-1,
    .row-1 .image-2 {
      width: 28%;
    }
    .row-1 .image-3 {
      width: 44%;
    }

    .row-2 .image-4 {
      width: 44%;
    }
    .row-2 .image-5 {
      width: 20%;
    }
    .row-2 .image-6 {
      width: 44%;
    }

    .row-3 .image-7 {
      width: 20%;
    }
    .row-3 .image-8 {
      width: 44%;
    }
    .row-3 .image-9 {
      width: 44%;
    }
  }

  .image-item {
    position: relative;
    height: 100%;
    width: 100%;
  }
`;

interface BlogCategoryGallery extends BlogCategoryImage {
  alt: string;
  className: string;
}

interface ExploreByInterestProps {
  blogs_category_images: BlogCategoryGallery[];
}

export default function ExploreByInterest({
  blogs_category_images
}: ExploreByInterestProps) {
  const [showMore, setShowMore] = useState(false);

  const [updatedImagesGallery, setUpdatedImagesGallery] = useState<
    BlogCategoryGallery[]
  >([]);
  const isMobileOrTablet = useMediaQuery(1023);

  useEffect(() => {
    if (Array.isArray(blogs_category_images)) {
      const mappedImages = blogs_category_images.map((item, index) => ({
        ...item,
        alt: `${item.name}-image`,
        className: `image-${index + 1}`
      }));
      setUpdatedImagesGallery(mappedImages);
    }
  }, [blogs_category_images]);

  const showImagesInRow = isMobileOrTablet
    ? showMore
      ? 8
      : 5
    : showMore
    ? 5
    : 3;

  return (
    <div className="flex flex-col gap-[22px] sm:gap-[40px] xl:gap-[60px]">
      <h2 className="text-black2 text-xl sm:text-[22px] md:text-[26px] lg:text-[26px] 2xl:text-3xl 3xl:text-4xl font-semibold">
        Explore by Interest
      </h2>
      <ImageGridContainer>
        {Array.from({
          length: showImagesInRow
        }).map((_, rowIndex) => {
          // Define the slice range based on the row index
          const sliceStart = isMobileOrTablet
            ? rowIndex === 0
              ? 0 // Row 1: Images 0-1
              : rowIndex === 1
              ? 2 // Row 2: Images 2
              : rowIndex === 2
              ? 3 // Row 3: Images 3-4
              : rowIndex === 3
              ? 5 // Row 4: Images 5-6
              : rowIndex === 4
              ? 7 // Row 5: Images 7-8
              : rowIndex === 5
              ? 9 // Row 6: Images 9-10 (same as Row 4)
              : rowIndex === 6
              ? 11 // Row 7: Images 11 (same as Row 2)
              : 12 // Row 8: Images 12-13 (same as Row 3)
            : rowIndex * 3;

          const sliceEnd = isMobileOrTablet
            ? rowIndex === 0
              ? 2 // Row 1: Images 0-1
              : rowIndex === 1
              ? 3 // Row 2: Images 2
              : rowIndex === 2
              ? 5 // Row 3: Images 3-4
              : rowIndex === 3
              ? 7 // Row 4: Images 5-6
              : rowIndex === 4
              ? 9 // Row 5: Images 7-8
              : rowIndex === 5
              ? 11 // Row 6: Images 9-10
              : rowIndex === 6
              ? 12 // Row 7: Images 11
              : 14 // Row 8: Images 12-13
            : rowIndex * 3 + 3;

          return (
            <div className={`row row-${rowIndex + 1}`} key={rowIndex}>
              {updatedImagesGallery
                ?.slice(sliceStart, sliceEnd)
                ?.map((category, index) => (
                  <Link
                    key={index}
                    className={`image-item ${category.className} group cursor-pointer`}
                    href={`/blog/category/${category.id}`}
                  >
                    <div className="relative w-full h-full">
                      <Image
                        src={category.image}
                        alt={category.alt}
                        fill
                        style={{ objectFit: 'cover' }}
                        className="w-full h-full"
                      />
                    </div>

                    <div className="absolute inset-0 bg-black transition-opacity duration-300 opacity-20 sm:opacity-0 sm:group-hover:opacity-40"></div>
                    <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 mb-4 text-white text-sm msm:text-base msm:leading-7 sm:text-[21px] 2xl:text-2xl font-semibold capitalize text-center">
                      {category.name}
                    </div>
                  </Link>
                ))}
            </div>
          );
        })}
        {/* See More / See Less Button */}
        <div className="text-right mt-4">
          <button
            onClick={() => setShowMore(prev => !prev)}
            className="text-black13 font-semibold cursor-pointer hover:underline text-sm lg:text-base"
          >
            {showMore ? 'See Less' : 'See More'}
          </button>
        </div>
      </ImageGridContainer>
    </div>
  );
}
